import { Logger } from '@/Logger';

import { TeoError } from '../../errors/TeoError';
import { TeoWritableStore } from '../../state/TeoStore';
import type {
  AiQuickSuggestionsData,
  AssistantInstruction,
  EditHistoryParagraph,
  SuggestionPreset,
} from '../../TeoApi';
import type { TeoText } from '../TeoText';
import type { AIHandler } from './AIHandler';
import {
  DEFAULT_LOADING_MESSAGE,
  getAISuggestionPrompt,
  getPresetLoadingMessage,
} from './aiSuggestionPresets';
import { AutoSuggestionHistory, type AutoAiHistoryEntry } from './AutoSuggestionHistory';
import {
  hasEditorParagraphs,
  prepareQuickRequest,
  quickSuggestionPreset,
  sendQuickRequest,
  type RequestedParagraph,
} from './quickSuggestionRequest';

const LOG = Logger('AutoSuggestionHandler');

const FULL_TEXT_PARAGRAPH_ID = 'full_text';
const REWRITE_PRESET = 'rewrite_tpi' satisfies SuggestionPreset;
const REWRITE_WORD_MIN = 200;
const REWRITE_INDEX_MAX = 50;

export type { AutoAiHistoryEntry };

/** How long a run waits for the editor to report the replaced paragraphs. */
const TEXT_UPDATE_TIMEOUT = 300;

/** How long a run waits for the editor to put the markers of a new analysis on its text. */
const MARKER_TIMEOUT = 3000;

/** How many runs in a row may leave the TPI where it is before the loop gives up. */
const UNCHANGED_LIMIT = 3;

/** A smaller change of the TPI counts as none, because the AI is then only trading words. */
const TPI_EPSILON = 0.1;

/** What a run is busy with, which decides what stopping it can still do. */
export type AutoSuggestionPhase = 'idle' | 'suggesting' | 'analyzing';

/**
 * How the work was asked to end.
 * - `hard`: right away, which aborts a suggestion request that is in flight.
 * - `soft`: after the suggestion that is currently loading has been applied and analyzed.
 */
export type AutoStopRequest = 'none' | 'soft' | 'hard';

/**
 * What a caller can decide about the work. Everything it leaves out keeps its default, and
 * a single run ignores the two that only tell a loop when to end.
 */
export type AutoSuggestionOptions = {
  /** Whether the editor highlights what the AI replaced. Defaults to `true`. */
  markChanges?: boolean;
  /** The TPI from which on a loop is done. Defaults to the target of the configuration. */
  tpiTarget?: number;
  /** The most runs a loop makes, so that it ends even while the TPI keeps moving. */
  maxRuns?: number;
  /**
   * Sent instead of the preset of the current instruction. Together with `instruction`,
   * this is what a caller uses when it already knows the prompt, such as the debug tool.
   */
  preset?: SuggestionPreset;
  /**
   * Sent alongside or instead of a preset. When both are set, the API keeps the rest of
   * the preset and overwrites its instruction. Omit when empty.
   */
  instruction?: string;
};

/** An instruction the AI refused during a run, with the reason it gave. */
export type SkippedInstruction = {
  id: number;
  reason: string;
};

/**
 * Why a run or loop stopped.
 * `noInstructions` is the case where skip reasons will later be summarized, once that
 * request exists: the AI ran out of tasks before the TPI target was reached.
 */
export type AutoEndReason =
  | 'applied'
  | 'noSuggestions'
  | 'noInstructions'
  | 'targetReached'
  | 'tpiUnchanged'
  | 'maxRuns'
  | 'stopped';

/** Everything the UI shows of the automatic optimization. */
export class AutoSuggestionState {
  isRunning = false;
  phase: AutoSuggestionPhase = 'idle';
  /** How many paragraphs the last run replaced. */
  appliedCount = 0;
  /** True once a run has finished, even if the AI changed nothing. */
  hasRun = false;
  /** True while runs are repeated. */
  isLooping = false;
  /** How many runs the current or last loop has finished. */
  loopRuns = 0;
  stopRequest: AutoStopRequest = 'none';
  /** Instructions this run skipped because the AI could not fulfill them. */
  skippedInstructions: SkippedInstruction[] = [];
  /** Why the last run or loop stopped. Cleared when a new one starts. */
  endReason: AutoEndReason | undefined = undefined;
  /**
   * User-facing explanation of {@link endReason}. Empty until a summary is fetched;
   * planned for `noInstructions`.
   */
  endReasonMessage = '';

  // --- ui state
  runningInstructionTitle: string | null = null;
  /** Applied rewrites, newest first, with the session original last. */
  uiHistory: AutoAiHistoryEntry[] = [];
  /**
   * History entry shown in the editor. `null` follows the newest entry, which is the only
   * one a manual edit or a loop may change.
   */
  activeHistoryId: string | null = null;
  /**
   * Id of the text a run or loop works on while it lasts. The editor mounts a hidden editor
   * for it, because the work text needs one to read and replace its paragraphs.
   */
  workTextId: number | null = null;
}

class AutoSuggestionHandlerStore extends TeoWritableStore<AutoSuggestionState> {
  constructor() {
    super(new AutoSuggestionState());
    this.__debug = true;
  }

  getHistoryById(id: string): AutoAiHistoryEntry | undefined {
    return this.state.uiHistory.find((entry) => entry.id === id);
  }

  getLatestHistoryEntry(): AutoAiHistoryEntry | undefined {
    return this.state.uiHistory.at(0);
  }

  reset(): void {
    this.reinit(new AutoSuggestionState());
  }
}

/**
 * The quick suggestions without any review: one run replaces the paragraphs the AI chose
 * and analyzes the text afterwards, and a loop repeats that until the text is good enough.
 *
 * The suggestions are requested without their analysis, because nothing here ranks them.
 * The TPI that matters is the one of the analysis that follows, since it is measured on the
 * text with all replacements in it.
 */
export class AutoSuggestionHandler {
  /** The state the UI reads and subscribes to. */
  public readonly store = new AutoSuggestionHandlerStore();
  /** Applied steps for this session. The loop decides when one is recorded. */
  public readonly history: AutoSuggestionHistory;
  /** The suggestion request in flight, which a hard stop aborts. */
  private pendingRequest: AbortController | null = null;
  /**
   * The copy of the text a run or loop edits, so that the text the user sees can show any
   * history entry meanwhile. Only exists while a run or loop is going on.
   */
  private _workText: TeoText | null = null;

  constructor(
    private text: TeoText,
    private ai: AIHandler,
  ) {
    this.history = new AutoSuggestionHistory(this.text, this.store);
    this.text.events.addEventListener('gotAnalysis', () => this.handlePostAnalysis());
    this.text.events.addEventListener('analysisStateChanged', (options) => {
      this.handleAnalysisTrigger(options?.trigger);
    });
  }

  exitAutoMode(): void {
    this.stop();
    this.text.store.setStateValue('performanceMode', 'manual');
  }

  get isRunning(): boolean {
    return this.store.state.isRunning;
  }

  get isLooping(): boolean {
    return this.store.state.isLooping;
  }

  get workText(): TeoText | null {
    return this._workText;
  }

  /** The text runs read and edit: the work text while it exists, otherwise the text itself. */
  private get work(): TeoText {
    return this._workText ?? this.text;
  }

  /** False while no instruction names a preset, which leaves nothing to ask the AI for. */
  get canRun(): boolean {
    return quickSuggestionPreset(this.text) !== null;
  }

  /**
   * Repeats runs until the text reaches the TPI target, the AI stops moving the TPI, or the
   * loop is stopped.
   * @param options Passed on to every run of the loop.
   * @returns The number of runs it made.
   */
  async startLoop(options: AutoSuggestionOptions = {}): Promise<number> {
    if (this.isLooping || this.isRunning) return 0;
    const { maxRuns, tpiTarget, ...runOptions } = this.resolveOptions(options);

    this.store.setStateValue('stopRequest', 'none');
    this.store.setStateValue('loopRuns', 0);
    this.store.setStateValue('isLooping', true);
    this.clearEndReason();
    let runs = 0;
    let unchanged = 0;
    let loopReason: AutoEndReason | undefined;
    try {
      // Suggestions and the target check both read the current analysis. A result from
      // before the last edit would send the loop after the wrong instructions.
      if (!(await this.ensureCurrentAnalysis())) {
        if (this.store.state.stopRequest !== 'none') loopReason = 'stopped';
        return runs;
      }

      this.beginEditSession();
      this.beginWork();
      // Auto mode always goes for the highest priority instruction.
      this.work.analysis?.updateCurrentInstruction(undefined);
      while (runs < maxRuns && !this.reachedTarget(tpiTarget)) {
        const tpiBefore = this.work.analysis?.getTPI();
        const applied = await this.run(runOptions);
        if (applied === 0) {
          loopReason =
            this.store.state.stopRequest !== 'none' ? 'stopped' : this.store.state.endReason;
          break;
        }
        runs++;
        this.store.setStateValue('loopRuns', runs);
        if (this.reachedTarget(tpiTarget)) {
          loopReason = 'targetReached';
          break;
        }
        unchanged = this.tpiMoved(tpiBefore) ? 0 : unchanged + 1;
        if (unchanged >= UNCHANGED_LIMIT) {
          LOG.dev(`The TPI did not move in ${unchanged} runs.`);
          loopReason = 'tpiUnchanged';
          break;
        }
        if (this.store.state.stopRequest !== 'none') {
          loopReason = 'stopped';
          break;
        }
      }
      if (!loopReason) {
        loopReason = this.reachedTarget(tpiTarget) ? 'targetReached' : 'maxRuns';
      }
    } catch {
      // the run already reported the failure
    } finally {
      this.endWork();
      if (loopReason) this.store.setStateValue('endReason', loopReason);
      this.store.setStateValue('isLooping', false);
      this.store.setStateValue('stopRequest', 'none');
    }
    return runs;
  }

  /**
   * Stops right away: a suggestion request in flight is aborted, so nothing of it is applied.
   * During the analysis there is nothing to abort, which makes this wait for it like a soft
   * stop does.
   */
  stop(): void {
    this.store.setStateValue('stopRequest', 'hard');
    this.pendingRequest?.abort();
    // TODO: maybe make this return a promise until analysis is done?
  }

  /** Stops once the suggestion that is currently loading has been applied and analyzed. */
  stopSoft(): void {
    // A hard stop asks for less work than this, so it is not taken back.
    if (this.store.state.stopRequest === 'hard') return;
    this.store.setStateValue('stopRequest', 'soft');
  }

  reset(): void {
    this.stop();
    this.store.reset();
  }

  exit(): void {
    this.stop();
    const finalItem = this.store.getLatestHistoryEntry();
    if (finalItem) {
      this.history.restoreHistory(finalItem);
    }
    this.store.reset();
    this.text.diff.hide();
  }

  /**
   * Requests suggestions, applies them without asking, and analyzes the result.
   * @returns The number of paragraphs that were replaced.
   */
  async run(options: AutoSuggestionOptions = {}): Promise<number> {
    if (this.isRunning) return 0;

    const { markChanges } = this.resolveOptions(options);
    // A stop only ends what was running when it was pressed.
    if (!this.isLooping) this.store.setStateValue('stopRequest', 'none');

    const abort = new AbortController();
    this.pendingRequest = abort;
    this.store.setStateValue('isRunning', true);
    this.store.setStateValue('phase', 'suggesting');
    this.clearEndReason();
    // Inside a loop, the loop owns the session and the work text.
    const standalone = !this.isLooping;
    if (standalone) this.beginEditSession();
    try {
      if (standalone) this.beginWork();
      const promptOverride =
        options.preset !== undefined || options.instruction !== undefined ?
          { preset: options.preset, instruction: options.instruction }
        : undefined;

      if (!promptOverride && this.shouldRewriteFullText()) {
        const confirmed = await this.confirmFullRewrite(abort.signal);
        if (abort.signal.aborted) {
          this.finishRun(0, 'stopped');
          return 0;
        }
        if (confirmed) {
          return await this.runFullRewrite(markChanges, abort);
        }
      }

      await this.waitForParagraphs();

      while (true) {
        // Re-select every time: a new analysis or the assistant UI can put a skipped
        // instruction back as current without the skip list.
        if (!promptOverride && !this.pickInstruction(this.skippedInstructionIds())) {
          this.finishRun(0, 'noInstructions');
          return 0;
        }
        const request = prepareQuickRequest(this.work, promptOverride);
        if (!request) {
          if (this.store.state.skippedInstructions.length > 0) {
            this.finishRun(0, 'noInstructions');
          }
          return 0;
        }

        const presetLoadingMessage =
          request.preset ? getPresetLoadingMessage(request.preset) : DEFAULT_LOADING_MESSAGE;

        // The loading state blocks the editor, so it has to end before the replacements are
        // applied. The analysis afterwards brings the animation back.
        const response = await this.work.store.withLoading(() => {
          this.store.setStateValue('runningInstructionTitle', presetLoadingMessage);
          return sendQuickRequest(this.work, this.ai, this.history.withEditHistory(request), {
            omitAnalysis: true,
            signal: abort.signal,
          });
        }, presetLoadingMessage);
        this.store.setStateValue('runningInstructionTitle', null);
        if (abort.signal.aborted) {
          this.finishRun(0, 'stopped');
          return 0;
        }

        const instruction = this.work.analysis?.currentInstruction;
        const edited = this.applySuggestions(response, request.paragraphs);
        if (edited.length > 0) {
          this.store.setStateValue('phase', 'analyzing');
          await this.waitForTextUpdate();
          await this.analyzeAutoApply();
          this.recordStep(markChanges, instruction, edited);
          this.finishRun(edited.length, 'applied');
          return edited.length;
        }

        const skip = this.skipUnableInstruction(response, promptOverride);
        if (skip === 'retry' && !abort.signal.aborted) continue;
        this.finishRun(0, skip === 'retry' || abort.signal.aborted ? 'stopped' : skip);
        return 0;
      }
      // hier history machen.
    } catch (error) {
      // The request was stopped, which is what the user asked for and not a failure.
      if (abort.signal.aborted) {
        this.finishRun(0, 'stopped');
        return 0;
      }
      LOG.error('The automatic optimization failed:', error);
      this.text.core.notifications.addError(error);
      throw error;
    } finally {
      if (standalone) this.endWork();
      this.pendingRequest = null;
      this.store.setStateValue('phase', 'idle');
      this.store.setStateValue('isRunning', false);
    }
  }

  getTpiTarget(options?: Pick<AutoSuggestionOptions, 'tpiTarget'>): number {
    return options?.tpiTarget ?? this.text.core.staticConfig.tpiTarget;
  }

  /**
   * Replaces every paragraph the AI rewrote.
   *
   * The response may hold several suggestions for the same paragraph. Nothing here can rank
   * them without their analysis, so the first one wins.
   * @returns The replaced paragraphs, with the text after the rewrite.
   */
  private applySuggestions(
    data: AiQuickSuggestionsData,
    paragraphs: RequestedParagraph[],
  ): EditHistoryParagraph[] {
    const byId = new Map(paragraphs.map((paragraph) => [paragraph.id, paragraph]));
    const replacements = new Map<string, string>();
    const edited: EditHistoryParagraph[] = [];
    data.suggestions.forEach((suggestion) => {
      const paragraph = byId.get(suggestion.id);
      if (!paragraph) {
        LOG.warn('Received a suggestion for an unknown paragraph:', suggestion.id);
        return;
      }
      if (replacements.has(paragraph.markerId)) return;
      replacements.set(paragraph.markerId, suggestion.text);
      edited.push({ id: paragraph.id, text: suggestion.text });
    });
    if (replacements.size === 0) return [];

    replacements.forEach((replacement, markerId) => {
      this.work.markers.replaceMarkerContent(markerId, replacement);
    });

    return edited;
  }

  /** Fills in the loop defaults the caller left out. Prompt fields stay as given. */
  private resolveOptions(
    options: AutoSuggestionOptions,
  ): Required<Pick<AutoSuggestionOptions, 'markChanges' | 'tpiTarget' | 'maxRuns'>> {
    return {
      markChanges: options.markChanges ?? true,
      tpiTarget: options.tpiTarget ?? this.text.core.staticConfig.tpiTarget,
      maxRuns: options.maxRuns ?? 20,
    };
  }

  /**
   * Analyzes when the result is older than the last edit, so the loop that follows reads
   * the text as it is now. A stop during this analysis is noticed once it returns, because
   * there is no request here that can be aborted.
   * @returns False when the result is still stale, or the loop was stopped while analyzing.
   */
  private async ensureCurrentAnalysis(): Promise<boolean> {
    if (this.text.analysisState.isCurrent) return true;
    this.store.setStateValue('isRunning', true);
    this.store.setStateValue('phase', 'analyzing');
    try {
      await this.text.analyze({ trigger: 'auto_suggestion' });
      const current = this.text.analysisState.isCurrent;
      // This analysis read the editor text, before any auto step of the loop.
      if (current) this.history.commitManualEdit();
      return this.store.state.stopRequest === 'none' && current;
    } finally {
      this.store.setStateValue('isRunning', false);
      if (this.store.state.stopRequest !== 'none' || !this.text.analysisState.isCurrent) {
        this.store.setStateValue('phase', 'idle');
      }
    }
  }

  /** True once the text is as good as the loop was asked to get it. */
  private reachedTarget(tpiTarget: number): boolean {
    const tpi = this.work.analysis?.getTPI();
    return tpi !== undefined && tpi >= tpiTarget;
  }

  /** True when the analysis that just arrived moved the TPI away from the given one. */
  private tpiMoved(tpiBefore: number | undefined): boolean {
    const tpiAfter = this.work.analysis?.getTPI();
    if (tpiBefore === undefined || tpiAfter === undefined) return true;
    return Math.abs(tpiAfter - tpiBefore) >= TPI_EPSILON;
  }

  /**
   * Records the current instruction as unfulfillable and selects the next one, when the AI
   * returned no suggestions and a reason. Does nothing when the caller chose the prompt
   * itself, because then there is no next instruction to fall back to.
   * @returns `retry` when another instruction is now selected, otherwise why the run ends.
   */
  private skipUnableInstruction(
    response: AiQuickSuggestionsData,
    promptOverride: AutoSuggestionOptions | undefined,
  ): 'retry' | AutoEndReason {
    if (promptOverride) return 'noSuggestions';
    if (response.suggestions.length > 0 || !response.unable_reason) return 'noSuggestions';
    const instruction = this.work.analysis?.currentInstruction;
    if (!instruction) return 'noInstructions';

    const skipped = [
      ...this.store.state.skippedInstructions,
      { id: instruction.id, reason: response.unable_reason },
    ];
    this.store.setStateValue('skippedInstructions', skipped);
    LOG.dev(`Instruction ${instruction.id} could not be fulfilled: ${response.unable_reason}`);

    const next = this.pickInstruction(skipped.map((entry) => entry.id));
    return next !== undefined && next.id !== instruction.id ? 'retry' : 'noInstructions';
  }

  /**
   * Selects the instruction to follow, ignoring those this run already found unfulfillable.
   * A loop always takes the highest remaining priority; a single run keeps the dimension.
   */
  private pickInstruction(instructionsToSkip: number[] = []): AssistantInstruction | undefined {
    const analysis = this.work.analysis;
    if (!analysis) return;
    const dimension = this.isLooping ? undefined : this.text.ui.state.dimension;
    return analysis.updateCurrentInstruction(dimension, instructionsToSkip, true);
  }

  /**
   * Waits until the editor hands out the paragraphs of the current analysis. It attaches the
   * markers of a fresh analysis with a delay, and until it has, the text looks to this
   * handler as if it had no paragraphs at all.
   */
  private waitForParagraphs(): Promise<void> {
    const work = this.work;
    if (hasEditorParagraphs(work)) return Promise.resolve();
    return new Promise((resolve) => {
      let removeListener = () => {};
      const finish = () => {
        clearTimeout(timeout);
        removeListener();
        resolve();
      };
      const timeout = setTimeout(finish, MARKER_TIMEOUT);
      // Other marker groups are attached as well, so every report is a reason to look again.
      removeListener = work.markers.events.addEventListener('markersAttached', () => {
        if (hasEditorParagraphs(work)) finish();
      });
    });
  }

  /**
   * Waits for the editor to report the replaced paragraphs, so that the analysis is not
   * requested for the text as it was before. Gives up after a moment, because a suggestion
   * that matches the text it replaces changes nothing to wait for.
   */
  private waitForTextUpdate(): Promise<void> {
    const work = this.work;
    return new Promise((resolve) => {
      const removers: (() => void)[] = [];
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        removers.forEach((remove) => remove());
        resolve();
      };
      const timeout = setTimeout(finish, TEXT_UPDATE_TIMEOUT);
      removers.push(
        work.events.addEventListener('textChanged', finish),
        work.events.addEventListener('textChangedExternal', finish),
      );
    });
  }

  /** Forgets the last run, whose paragraphs the new analysis has replaced. */
  private handlePostAnalysis(): void {
    this.store.setStateValue('appliedCount', 0);
    this.store.setStateValue('hasRun', false);
    // Skips belong to the auto session, not to one analysis. Clearing them here made the
    // next loop request try a refused instruction again.
    if (!this.isRunning && !this.isLooping) {
      this.store.setStateValue('skippedInstructions', []);
      this.clearEndReason();
    }
  }

  /**
   * A non-auto analysis of a changed editor becomes a manual step. `auto_suggestion` is
   * handled by the caller: the loop start commits from {@link ensureCurrentAnalysis}, and
   * an apply is recorded as an auto step. A missing trigger is a replayed history result.
   */
  private handleAnalysisTrigger(
    trigger: 'button_click' | 'auto_suggestion' | null | undefined,
  ): void {
    if (trigger === undefined || trigger === 'auto_suggestion') return;
    if (this.isRunning || this.isLooping) return;
    this.history.commitManualEdit();
  }

  private finishRun(applied: number, reason: AutoEndReason): void {
    this.store.update(() => ({
      appliedCount: applied,
      hasRun: true,
      endReason: reason,
    }));
  }

  private clearEndReason(): void {
    this.store.update(() => ({
      endReason: undefined,
      endReasonMessage: '',
    }));
  }

  /** Remembers the text and TPI from before the first apply of this session. */
  private beginEditSession(): void {
    this.history.ensureSessionBaseline();
    this.store.setStateValue('skippedInstructions', []);
  }

  /**
   * Copies the text into a temporary work text for the run or loop that starts now. The
   * copy carries the analysis, so it has to be taken once the analysis is current.
   */
  private beginWork(): void {
    this.endWork();
    const workText = this.text.core.createTemporaryText();
    workText.updateTextData(this.text.getTextData());
    this._workText = workText;
    this.store.setStateValue('workTextId', workText.id);
  }

  /** Drops the work text. What it holds beyond the last recorded step is discarded. */
  private endWork(): void {
    const workText = this._workText;
    if (!workText) return;
    this._workText = null;
    this.store.setStateValue('workTextId', null);
    workText.dispose();
  }

  /**
   * Records the analyzed work text as a history step, and shows it when the user follows
   * the newest entry. The step has to be recorded before {@link finishRun}, because showing
   * it replays its analysis, which resets what the last run did.
   */
  private recordStep(
    markChanges: boolean,
    instruction: AssistantInstruction | undefined,
    paragraphs: EditHistoryParagraph[],
    instructionTitle?: string,
  ): void {
    this.history.recordAppliedStep(this.work, instruction, paragraphs, instructionTitle);
    this.history.followNewest(markChanges);
  }

  private skippedInstructionIds(): number[] {
    return this.store.state.skippedInstructions.map((entry) => entry.id);
  }

  /**
   * True on a new auto session when the text is long and both AI and VI are still low,
   * which is when a full rewrite is tried before paragraph-level quick requests.
   */
  private shouldRewriteFullText(): boolean {
    if (this.history.hasHistory()) return false;
    const analysis = this.work.analysis;
    if (!analysis) return false;
    if (analysis.getNumWords() <= REWRITE_WORD_MIN) return false;
    const ai = analysis.getIndices().abstractness?.score;
    const vi = analysis.getIndices().comprehensibility?.score;
    if (ai === undefined || vi === undefined) return false;
    return ai < REWRITE_INDEX_MAX && vi < REWRITE_INDEX_MAX;
  }

  /**
   * Asks before a full rewrite is requested. Declining, or closing the dialog, leaves this
   * run to continue with paragraph suggestions. A stop aborts the signal, closes the dialog,
   * and leaves auto mode.
   */
  private confirmFullRewrite(signal: AbortSignal): Promise<boolean> {
    if (signal.aborted) return Promise.resolve(false);

    return new Promise((resolve) => {
      let settled = false;
      let removeListener = () => {};
      const finish = (accepted: boolean) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', onAbort);
        removeListener();
        resolve(accepted);
      };
      const onAbort = () => {
        this.text.core.notifications.close(id);
        finish(true);
      };

      // const onExit = () => {
      //   this.text.core.notifications.close(id);
      //   this.exitAutoMode();
      //   finish(false);
      // };

      const id = this.text.core.notifications.add({
        mode: 'info',
        title: 'Der TPI liegt weit unter dem Ziel-Wert!',
        message: 'Soll im ersten Schritt der gesamte Text optimiert werden?',
        buttons: [
          // {
          //   label: 'Abbrechen',
          //   variant: 'secondary',
          //   onclick: () => onExit(),
          // },
          {
            label: 'Nein, absatzweise',
            variant: 'secondary',
            onclick: () => finish(false),
          },
          {
            label: 'Ja',
            variant: 'primary',
            autofocus: true,
            onclick: () => finish(true),
          },
        ],
      });

      // Closing the dialog without a button, such as the overlay, is a decline. The button
      // resolves first, so the close that follows cannot overwrite that answer.
      removeListener = this.text.core.notifications.addEventListener(
        'notificationsChanged',
        (notifications) => {
          if (!notifications.some((notification) => notification.id === id)) finish(false);
        },
      );
      signal.addEventListener('abort', onAbort);
    });
  }

  /**
   * Replaces the whole text with the `rewrite_tpi` suggestion, then analyzes like a normal
   * auto run. Does not fall through to a quick request when the AI returns nothing.
   */
  private async runFullRewrite(markChanges: boolean, abort: AbortController): Promise<number> {
    const preset = getAISuggestionPrompt(REWRITE_PRESET);
    const loadingMessage = getPresetLoadingMessage(REWRITE_PRESET);

    const work = this.work;
    const response = await work.store.withLoading(() => {
      this.store.setStateValue('runningInstructionTitle', loadingMessage);
      return this.text.core.api.getAiSuggestionV2(
        {
          context: { text: work.text },
          ...this.ai.getRequestBase(),
          preset: REWRITE_PRESET,
        },
        { signal: abort.signal },
      );
    }, loadingMessage);
    this.store.setStateValue('runningInstructionTitle', null);

    if (abort.signal.aborted) {
      this.finishRun(0, 'stopped');
      return 0;
    }
    if (TeoError.isError(response)) {
      throw new Error(`API error: ${response.message}`);
    }

    const rewritten = response.suggestions[0]?.text;
    if (!rewritten || rewritten === work.text) {
      this.finishRun(0, 'noSuggestions');
      return 0;
    }

    this.store.setStateValue('phase', 'analyzing');
    this.applyFullRewrite(rewritten);
    await this.waitForTextUpdate();
    await this.analyzeAutoApply();
    this.recordStep(
      markChanges,
      undefined,
      [{ id: FULL_TEXT_PARAGRAPH_ID, text: rewritten }],
      preset.title,
    );
    this.finishRun(1, 'applied');
    return 1;
  }

  private applyFullRewrite(rewritten: string): void {
    const work = this.work;
    const wasEditable = work.isEditable;
    work.isEditable = true;
    work.updateText(rewritten);
    work.isEditable = wasEditable;
  }

  /** The analysis after an auto apply. It must not be stored as a manual edit. */
  private analyzeAutoApply(): Promise<void> {
    return this.work.analyze({ trigger: 'auto_suggestion' });
  }
}
